import {
  TableCell,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableBody,
  TableProps,
  Typography,
  Collapse,
  IconButton,
  TableFooter,
  TablePagination,
  LinearProgress,
  SxProps,
  Theme,
} from "@mui/material";
import { TableContainer } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Icon } from "../Icon";
import { Box } from "../Box";
import { getRotationStyle } from "../../../utils/themeUtils";
import { palette } from "../../../styles/theme";
import {
  ChangeEvent,
  Fragment,
  memo,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  MouseEvent,
} from "react";
import { Input } from "../Input";
import TablePaginationActions from "./components/TablePaginationActions";
import { Select } from "../Select/Select";
import OverlayScrollbarWrapper from "../OverlayScrollbar/OverlayScrollbarWrapper";
import { debounce } from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import TableSkeleton from "./components/TableSkeleton";

const collapsibleColumnId = "collapsible";
const actionColumnId = "action";

export interface TableColumn<T> {
  id: keyof T | typeof collapsibleColumnId | typeof actionColumnId | string;
  labelKey: string;
  width?: number | string;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  hide?: boolean;
  customSortKey?: string;
  colSpan?: number;
  render?: (row: T) => ReactNode;
  format?: (value: any) => string;
  openByDefault?: (row: T) => boolean;
}

export interface SortBy {
  columnId: string;
  direction: "asc" | "desc";
}

interface Props<T extends { id: string }> extends TableProps {
  columns: TableColumn<T>[];
  data: T[];
  sortBy?: SortBy;
  isRefetching?: boolean;
  isInitialLoading?: boolean;
  handleSort?: (sortBy?: SortBy) => void;
  totalCount?: number;
  rowsPerPage?: number;
  page?: number;
  handleSearch?: (searchTerm: string) => void;
  handleChangePage?: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage?: (event: ChangeEvent<HTMLInputElement>) => void;
  containerSx?: SxProps<Theme>;
}

const Table = ({
  columns,
  data,
  totalCount,
  isRefetching,
  isInitialLoading,
  sortBy,
  handleSort,
  rowsPerPage = 50,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSearch,
  containerSx,
  ...props
}: Props<any>) => {
  const { t, i18n } = useTranslation();
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredColumns = columns.filter((column) => !column.hide);

  // eslint-disable-next-line
  const debouncedHandleSearch = useCallback(
    debounce((value: string) => {
      handleSearch?.(value);
    }, 1000),
    [handleSearch]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  const onChangeSearchTerm = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTerm(e.target.value);
    handleChangePage?.(null, 0);
    debouncedHandleSearch(e.target.value);
  };

  if (isInitialLoading) {
    return <TableSkeleton />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...containerSx,
      }}
    >
      <TableContainer
        {...props}
        sx={{
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          ...props.sx,
        }}
      >
        <OverlayScrollbarWrapper
          options={{
            scrollbars: { autoHide: "never" },
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <MuiTable
            sx={{
              borderCollapse: "separate",
              "& th, & td": {
                borderBottom: `1px solid ${palette.grey.lightGrey}`,
              },
            }}
            stickyHeader
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "white",
                "& th": {
                  backgroundColor: "white",
                  borderBottom: `2px solid ${palette.grey.lightGrey}`,
                },
              }}
            >
              {handleSearch && (
                <TableRow>
                  <TableCell
                    key="search"
                    align="left"
                    sx={{ width: "calc(100% - 8px)" }}
                    colSpan={filteredColumns.length}
                  >
                    <Input
                      fullWidth
                      value={searchTerm}
                      onChange={onChangeSearchTerm}
                      clearable
                      endAdornment={
                        <SearchIcon
                          sx={{ width: 24, height: 24 }}
                          htmlColor={palette.grey.nearBlack}
                        />
                      }
                      placeholder={t("general.search")}
                      containerSx={{
                        width: "calc(100% - 8px)",
                      }}
                      sx={{
                        width: "calc(100% - 8px)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {filteredColumns
                  .filter((column) => !column.hide)
                  .map((column) => {
                    const isSorted =
                      sortBy?.columnId === (column.customSortKey ?? column.id);
                    const rotationStyle = getRotationStyle(
                      sortBy?.direction === "desc" && isSorted
                    );
                    return (
                      <TableCell
                        key={column.id as string}
                        onClick={() => {
                          if (column.sortable) {
                            if (isSorted) {
                              handleSort?.({
                                columnId:
                                  column.customSortKey ?? (column.id as string),
                                direction:
                                  sortBy?.direction === "desc" ? "asc" : "desc",
                              });
                            } else {
                              handleSort?.({
                                columnId:
                                  column.customSortKey ?? (column.id as string),
                                direction: "asc",
                              });
                            }
                          }
                        }}
                        sx={{
                          width: column.width,
                        }}
                        align={column.align}
                      >
                        <Typography
                          variant="textSmBold"
                          sx={{
                            position: "relative",
                            width: "fit-content",
                            cursor: column.sortable ? "pointer" : "default",
                            "&:hover": {
                              ">div": {
                                display: "block",
                              },
                            },
                          }}
                        >
                          {column.labelKey && i18n.exists(column.labelKey)
                            ? t(column.labelKey)
                            : column.labelKey}
                          {column.sortable && (
                            <Box
                              sx={{
                                position: "absolute",
                                right: "-28px",
                                top: "50%",
                                ...rotationStyle,
                                transform: rotationStyle.transform,
                                display: isSorted ? "block" : "none",
                                height: "24px",
                                marginTop: "-12px",
                              }}
                            >
                              <Icon
                                icon="arrow_upward"
                                weight={500}
                                color={palette.grey.primary}
                              />
                            </Box>
                          )}
                        </Typography>
                      </TableCell>
                    );
                  })}
              </TableRow>
              {isRefetching && (
                <TableRow
                  sx={{
                    width: "100%",
                    height: 0,
                    p: 0,
                    right: 0,
                    left: 0,
                    position: "sticky",
                    bottom: 0,
                  }}
                >
                  <TableCell
                    colSpan={filteredColumns.length}
                    sx={{ width: "100%", height: 0, p: 0 }}
                  >
                    <LinearProgress
                      sx={{
                        minWidth: "200px",
                        width: "100%",
                        right: 0,
                        left: 0,
                        position: "absolute",
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableHead>

            <TableBody
              sx={{
                height: "100%",
                maxHeight: "100%",
                "& td": {
                  borderBottom: `1px solid ${palette.grey.lightGrey}`,
                },
              }}
            >
              {data.map((row: any, rowIndex: number) => {
                const isLastRow = rowIndex === data.length - 1;
                const collapsibleColumn = columns.find(
                  (column) => column.id === collapsibleColumnId
                );
                const defaultOpen =
                  collapsibleColumn?.openByDefault?.(row) ?? false;
                const isCollapsibleRowOpen =
                  openRowIds.includes(row.id) || defaultOpen;
                return (
                  <Fragment key={row.id ?? uuidv4()}>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        ...(isLastRow && {
                          "& td": { borderBottom: "none !important" },
                        }),
                        backgroundColor:
                          rowIndex % 2 === 0
                            ? palette.white
                            : palette.grey.lightBackground,
                      }}
                    >
                      {filteredColumns.map((column) => {
                        const isCollapsible = column.id === collapsibleColumnId;
                        return (
                          <TableCell
                            key={(column.id as string) || uuidv4()}
                            align={isCollapsible ? "center" : column.align}
                            sx={{
                              width: isCollapsible
                                ? "min-content"
                                : column.width,
                              padding: isCollapsible ? "16px 4px" : undefined,
                              textAlign: isCollapsible
                                ? "center"
                                : column.align,
                              "& .MuiTypography-root": {
                                textAlign: isCollapsible
                                  ? "center"
                                  : column.align,
                                width: "100%",
                                display: "block",
                              },
                            }}
                            colSpan={column.colSpan}
                          >
                            {isCollapsible ? (
                              <IconButton
                                onClick={() =>
                                  setOpenRowIds(
                                    isCollapsibleRowOpen ? [] : [row.id]
                                  )
                                }
                                sx={getRotationStyle(isCollapsibleRowOpen)}
                              >
                                <Icon icon="keyboard_arrow_down" />
                              </IconButton>
                            ) : column.render ? (
                              column.render(row)
                            ) : (
                              <Typography
                                variant="textRegular"
                                sx={{
                                  textAlign: isCollapsible
                                    ? "center"
                                    : column.align,
                                }}
                              >
                                {column.id in row
                                  ? (column.format?.(row[column.id]) ??
                                    String(row[column.id as keyof any]))
                                  : ""}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {collapsibleColumn && collapsibleColumn.render && (
                      <TableRow className="collapsible-row">
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={filteredColumns.length + 3}
                          key={`${row.id}-${collapsibleColumnId}`}
                        >
                          <Collapse
                            in={isCollapsibleRowOpen}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box>{collapsibleColumn.render(row)}</Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
            {rowsPerPage != null &&
              totalCount != null &&
              page != null &&
              handleChangePage != null &&
              handleChangeRowsPerPage != null && (
                <TableFooter
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "white",
                    zIndex: 2,
                    boxShadow: "0px -2px 5px rgba(0, 0, 0, 0.05)",
                    width: "100%",
                  }}
                >
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[
                        25,
                        50,
                        100,
                        { label: "All", value: totalCount },
                      ]}
                      colSpan={filteredColumns.length}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      slotProps={{
                        select: {
                          input: (
                            <Select
                              containerSx={{
                                maxWidth: "fit-content",
                                mr: 2,
                                ml: 1,
                              }}
                              sx={{
                                width: "fit-content",
                                margin: "1px !important",
                              }}
                            />
                          ),
                        },
                      }}
                      onPageChange={handleChangePage}
                      ActionsComponent={TablePaginationActions}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        border: "none",
                        "& .MuiTablePagination-toolbar": {
                          borderTop: `1px solid ${palette.grey.lightGrey}`,
                          padding: "8px",
                        },
                        "& td": {
                          borderBottom: "none",
                        },
                      }}
                    />
                  </TableRow>
                </TableFooter>
              )}
          </MuiTable>
        </OverlayScrollbarWrapper>
      </TableContainer>
    </Box>
  );
};

export default memo(Table) as <T extends { id: string }>(
  props: Props<T>
) => ReactNode;
